/* hs-eslint ignored failing-rules */
/* eslint-disable no-prototype-builtins */

import { pick, omit, isEmpty } from 'underscore';
import { getDeepValue, getDeepClone, setDeepValue } from './ObjectHelpers';
const LOGO_CONVERSIONS = {
  fromLegacyModule: module => {
    const clonedModule = getDeepClone(module);
    if (clonedModule.body.override_inherited_src) {
      const fieldsToMove = ['src', 'width', 'height', 'alt', 'suppress_company_name'];
      const img = pick(clonedModule.body, fieldsToMove);
      img.override_inherited_src = true;
      const newModuleBody = omit(clonedModule.body, 'override_inherited_src', ...fieldsToMove);
      clonedModule.body = newModuleBody;
      clonedModule.body.img = img;
    }
    return clonedModule;
  },
  toLegacyData: data => {
    if (data.img && data.img.override_inherited_src) {
      return omit(Object.assign({}, data, data.img), 'img');
    }
    return omit(Object.assign({}, data, {
      override_inherited_src: false,
      alt: '',
      src: '',
      height: -1,
      width: -1
    }), 'img');
  }
};
const IMAGE_CONVERSIONS = {
  fromLegacyModule: module => {
    const clonedModule = getDeepClone(module);
    const fieldsToMove = ['src', 'width', 'height', 'alt', 'size_type', 'max_width', 'max_height', 'loading'];
    clonedModule.body.img = pick(clonedModule.body, fieldsToMove);
    clonedModule.body.target = clonedModule.body.open_in_new_tab;
    clonedModule.body = omit(clonedModule.body, 'open_in_new_tab', ...fieldsToMove);
    return clonedModule;
  },
  toLegacyData: data => {
    const {
      target
    } = data;
    const newData = omit(Object.assign({}, data, data.img, {
      constrain_proportions: true
    }), 'img', 'target');

    // Reset all image fields on image remove
    if (!data.img || isEmpty(data.img)) {
      newData.src = '';
      newData.alt = '';
      newData.height = -1;
      newData.width = -1;
      newData.original_height = -1;
      newData.original_width = -1;
    }
    if (target != null) {
      newData.open_in_new_tab = target;
    }
    return newData;
  }
};
const FORM_CONVERSIONS = {
  fromLegacyModule: (module, bodyPath) => {
    const clonedModule = getDeepClone(module);
    let body = getDeepValue(clonedModule, bodyPath);
    const {
      followup_email_id
    } = body.form || {};
    body.form = {
      form_id: body.form_to_use,
      form_type: body.form_type,
      gotowebinar_webinar_key: body.gotowebinar_webinar_key,
      webinar_id: body.webinar_id,
      webinar_source: body.webinar_source,
      message: body.response_message,
      redirect_id: body.response_redirect_id,
      redirect_url: body.response_redirect_url,
      response_type: body.response_response_type
    };
    if (followup_email_id) {
      body.form.followup_email_id = followup_email_id;
    }
    body = omit(body, ['form_to_use', 'form_type', 'gotowebinar_webinar_key', 'webinar_id', 'webinar_source', 'response_message', 'response_redirect_id', 'response_redirect_url', 'response_response_type']);
    return setDeepValue(clonedModule, bodyPath, body);
  },
  toLegacyData: data => {
    const {
      form
    } = data;
    const {
      followup_email_id
    } = form || {};
    const formFields = form ? {
      form_to_use: form.form_id,
      form_type: form.form_type,
      gotowebinar_webinar_key: form.gotowebinar_webinar_key,
      webinar_id: form.webinar_id,
      webinar_source: form.webinar_source,
      response_message: form.message,
      response_redirect_id: form.redirect_id,
      response_redirect_url: form.redirect_url,
      response_response_type: form.response_type
    } : {};
    return followup_email_id ? Object.assign({}, data, formFields, {
      form: {
        followup_email_id
      }
    }) : omit(Object.assign({}, data, formFields), 'form');
  }
};
const GALLERY_CONVERSIONS = {
  fromLegacyModule: module => {
    const clonedModule = getDeepClone(module);
    let slides = clonedModule.body.slides;
    if (slides) {
      slides = slides.map(slide => {
        slide.img = {
          src: slide.img_src,
          alt: slide.alt_text
        };
        return omit(slide, 'img_src', 'alt_text');
      });
      clonedModule.body.slides = slides;
    }
    return clonedModule;
  },
  toLegacyData: data => {
    if (data.slides) {
      const slides = data.slides.map(slide => {
        if (slide.img) {
          return omit(Object.assign({}, slide, {
            img_src: slide.img.src,
            alt_text: slide.img.alt
          }), 'img');
        }
        return slide;
      });
      return Object.assign({}, data, {
        slides
      });
    }
    return data;
  }
};
const IMAGE_SLIDER_CONVERSIONS = {
  fromLegacyModule: GALLERY_CONVERSIONS.fromLegacyModule,
  toLegacyData: data => {
    // The old slider data also set these booleans which are required for the renderer to ouput
    // the correct HTML (https://git.hubteam.com/HubSpot/ContentModulesUI/blob/fcdcf690ba796fd32dae7446fe59379098e77a59/static/coffee/views/forms/imageSliderEditForm.coffee#L265-L277)
    if (data.version == null || data.version === 0) {
      if (data.display_mode === 'standard') {
        data = Object.assign({}, data, {
          only_thumbnails: false,
          with_thumbnail_nav: false,
          lightbox: false
        });
      } else if (data.display_mode === 'thumbnail') {
        data = Object.assign({}, data, {
          only_thumbnails: false,
          with_thumbnail_nav: true,
          lightbox: false
        });
      } else if (data.display_mode === 'lightbox') {
        data = Object.assign({}, data, {
          only_thumbnails: false,
          with_thumbnail_nav: true,
          lightbox: true
        });
      }
    }
    return GALLERY_CONVERSIONS.toLegacyData(data);
  }
};
const RSS_LISTING_CONVERSIONS = {
  fromLegacyModule: module => {
    const clonedModule = getDeepClone(module);
    clonedModule.body.rss_feed_type = clonedModule.body.is_external ? 'external' : 'blog';
    clonedModule.body = omit(clonedModule.body, 'is_external');
    return clonedModule;
  },
  toLegacyData: data => {
    const isExternal = data.rss_feed_type === 'external';
    return omit(Object.assign({}, data, {
      is_external: isExternal
    }), 'rss_feed_type');
  }
};
const SOCIAL_SHARING_CONVERSIONS = {
  fromLegacyModule: module => {
    const clonedModule = getDeepClone(module);
    // Make sure network link placeholders come through (and not blocked by empty strings)
    Object.keys(clonedModule.body).forEach(key => {
      if (clonedModule.body[key] && clonedModule.body[key].hasOwnProperty('custom_link_format') && clonedModule.body[key].custom_link_format === '') {
        delete clonedModule.body[key].custom_link_format;
      }
    });
    let pinImage;
    if (clonedModule.body.pinterest) {
      pinImage = clonedModule.body.pinterest.pinterest_media;
    }
    if (pinImage) {
      clonedModule.body.pinterest.pinterest_media = {
        src: pinImage
      };
    }
    return clonedModule;
  },
  toLegacyData: data => {
    let result = data;
    // From what I can undderstand, if `use_page_url` is missing and `link` is an empty string the
    // renderer's social sharing v1 logic will output empty HTML. So force `use_page_url` to be
    // be present for false-y custom links
    if (result && !result.link) {
      result.use_page_url = true;
    }
    const pinImage = result && result.pinterest && result.pinterest.pinterest_media && result.pinterest.pinterest_media.src;
    if (pinImage) {
      result = Object.assign({}, result, {
        pinterest: Object.assign({}, result.pinterest, {
          pinterest_media: pinImage
        })
      });
    }
    return result;
  }
};
const CHOICE_CONVERSIONS = {
  fromLegacyModule: module => {
    const clonedModule = getDeepClone(module);
    const newValue = {
      selectedValue: clonedModule.body.value,
      choices: clonedModule.extra_params.choices
    };
    clonedModule.body.value = newValue;
    return clonedModule;
  },
  toLegacyData: null
};
const CUSTOM_WIDGET_CONVERSIONS = {
  toLegacyData: (data, schema) => {
    // Flatten all the image field data from `imgFieldname.src` to `imgFieldname_src`
    // (while also intentionally leaving around the `imgFieldname` obj)
    const fields = schema.fields || [];
    fields.forEach(field => {
      if (field.type === 'image') {
        const fieldName = field.name;
        const imageFieldObj = data[fieldName];
        if (imageFieldObj) {
          Object.keys(imageFieldObj).forEach(imageSubKey => {
            data[`${fieldName}_${imageSubKey}`] = imageFieldObj[imageSubKey];
          });
        } else if (imageFieldObj === null) {
          data[`${fieldName}_src`] = '';
        }
      }
    });
    // Make sure we don't lose the widget_name, because cmv1 bodies are set and not merged
    // (and if `widget_name` disappears, the module will fail to render and be missing data from BE)
    data.widget_name = schema.name;
    return data;
  },
  fromLegacyModule: null
};
const CONVERSION_LOOKUP = {
  choice: CHOICE_CONVERSIONS,
  custom_widget: CUSTOM_WIDGET_CONVERSIONS,
  form: FORM_CONVERSIONS,
  gallery: GALLERY_CONVERSIONS,
  image_slider: IMAGE_SLIDER_CONVERSIONS,
  image: IMAGE_CONVERSIONS,
  linked_image: IMAGE_CONVERSIONS,
  logo: LOGO_CONVERSIONS,
  rss_listing: RSS_LISTING_CONVERSIONS,
  social_sharing: SOCIAL_SHARING_CONVERSIONS
};
export const fromLegacyModule = (module, bodyPath = ['body']) => {
  const conversionKey = module.type;
  if (module && CONVERSION_LOOKUP[conversionKey] && CONVERSION_LOOKUP[conversionKey].fromLegacyModule) {
    return CONVERSION_LOOKUP[conversionKey].fromLegacyModule(module, bodyPath);
  }
  return module;
};
export const toLegacyData = (moduleType, data, schema) => {
  if (CONVERSION_LOOKUP[moduleType] && CONVERSION_LOOKUP[moduleType].toLegacyData) {
    return CONVERSION_LOOKUP[moduleType].toLegacyData(data, schema);
  }
  return data;
};